import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { uuidv4 } from '@hevoio/hevo-ui-toolkit';
import { EntityAction } from '../../interfaces/entity-actions';
import { EntityMenuActionButton } from './EntityMenuActionButton';
import { EntityMenu } from './EntityMenu';

const MENU_OPEN_EVENT = 'entity-menu-opened';

export function EntityMenuActions<A extends string | number | symbol>({
  actions,
  onActionClick,
  className,
  menuLabel,
  menuIconSize
}: {
  actions: EntityAction<A>[];
  onActionClick: (actions: EntityAction<A>) => void;
  className?: string;
  menuLabel?: string;
  menuIconSize?: number;
}) {
  const menuId = useRef(`menu-actions-${uuidv4()}`);
  const [kebabMenuOpen, setKebabMenuOpen] = useState(false);

  useEffect(() => {
    const handleMenuOpened = (e: Event) => {
      if ((e as CustomEvent).detail !== menuId.current) {
        setKebabMenuOpen(false);
      }
    };
    document.addEventListener(MENU_OPEN_EVENT, handleMenuOpened);
    return () => document.removeEventListener(MENU_OPEN_EVENT, handleMenuOpened);
  }, []);

  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      <EntityMenuActionButton
        label={menuLabel || 'Actions'}
        target={menuId.current}
        onClick={() => {
          if (!kebabMenuOpen) {
            document.dispatchEvent(new CustomEvent(MENU_OPEN_EVENT, { detail: menuId.current }));
          }
          setKebabMenuOpen(prev => !prev);
        }}
        className={clsx(className)}
        iconSize={menuIconSize}
      />

      <EntityMenu
        target={menuId.current}
        open={kebabMenuOpen}
        onClose={() => setKebabMenuOpen(false)}
        placement='bottom-start'
        style={{ top: 20 }}
        actions={actions}
        onActionClick={onActionClick}
      />
    </>
  );
}
